import Konva from "konva";
import { GenericNodeEvents } from "./common";
import type { TableState } from "./types";
import { Seat2 } from "./seat";
import { v4 as uuidv4 } from "uuid";

const DEFAULT_SEAT_WIDTH = 10;
const DEFAULT_SEAT_HEIGHT = 10;
const DEFAULT_SEATS_GAP = 10;
const PADDING = 5;

export class Table extends GenericNodeEvents<Table, TableState> {
  private id!: string;
  private container!: Konva.Shape;
  private readonly blockGroup: Konva.Group;
  private readonly tableGroup: Konva.Group;
  private labelContainer!: Konva.Rect;
  private label!: Konva.Text;
  private state: TableState;
  private nextSeatNumber: number;
  readonly seats: Seat2[];

  get nodeId() {
    return this.id;
  }

  get currentState() {
    return this.state;
  }

  constructor(blockGroup: Konva.Group, state: TableState) {
    const group = new Konva.Group({
      draggable: state.draggable !== undefined ? state.draggable : true,
    });

    super(group, "table");
    this.state = state;
    this.target = this;

    this.blockGroup = blockGroup;
    this.tableGroup = group;
    this.nextSeatNumber = 1;

    this.seats = [];

    this.blockGroup.add(this.tableGroup);
    this.tableGroup.on("dragend", () => {
      this.state.position = this.tableGroup.position();
      if (this.onStateChangeCallback) {
        this.onStateChangeCallback(this.state);
      }
    });
  }

  create() {
    this.destroyChildren();
    this.initElements();

    this.state.id = this.state.id || uuidv4();
    this.id = this.state.id;

    const gap = DEFAULT_SEATS_GAP;
    const width = this.state.width || 50;
    const height =
      Math.max(
        this.state.seats.left.length,
        this.state.seats.right.length,
        this.state.seats.bottom.length,
        this.state.seats.top.length,
      ) *
      (DEFAULT_SEAT_WIDTH + gap);
    PADDING + PADDING;

    this.container.setAttrs({
      width,
      height,
      fill: this.state.background,
    });

    this.tableGroup.setAttrs({
      ...this.state.position,
      rotation: this.state.rotation,
      scaleX: this.state.scaleX || 1,
      scaleY: this.state.scaleY || 1,
    });

    this.tableGroup.on("transformend", () => {
      this.state.position = this.tableGroup.position();
      this.state.rotation = this.tableGroup.rotation();
      this.state.scaleX = this.tableGroup.scaleX();
      this.state.scaleY = this.tableGroup.scaleY();

      if (this.onStateChangeCallback) {
        this.onStateChangeCallback(this.state);
      }
    });

    this.state.seatsOrder.forEach((direction) => {
      if (!direction) return;

      this.state.seats[direction].forEach((seatState, i) => {
        if (!seatState.position) {
          seatState.position = { x: 0, y: 0 };
        }

        switch (direction) {
          case "left":
            seatState.position.y =
              i * (DEFAULT_SEAT_HEIGHT + gap) + PADDING * 2;
            seatState.position.x = -(gap * 2);
            break;
          case "right":
            seatState.position.y =
              this.container.height() -
              (i * (DEFAULT_SEAT_HEIGHT + gap) + PADDING * 2);
            seatState.position.x = this.container.width() + gap - 5;
            break;
          case "top":
            break;
          case "bottom":
            seatState.position.y = this.container.height() + PADDING * 2;
            seatState.position.x =
              i * (DEFAULT_SEAT_HEIGHT + gap) + PADDING * 2;
            break;
        }

        seatState.id = seatState.id || uuidv4();
        seatState.height = DEFAULT_SEAT_HEIGHT;
        seatState.width = DEFAULT_SEAT_WIDTH;
        seatState.label = seatState.label || this.getNextSeatNumber();
        seatState.fillText = "black";
        seatState.fill = "white";
        seatState.strokeWidth = 1;
        seatState.stroke = "black";
        seatState.borderRadius = seatState.borderRadius ?? 0;

        const seat = new Seat2(
          +seatState.label,
          this.tableGroup,
          seatState,
          this as any,
        );
        seat.create();

        this.seats.push(seat);
        if (this.onPressCallback) {
          seat.onPress(this.onPressCallback);
        }
      });
    });

    this.loadLabel();

    if (this.onStateChangeCallback) {
      this.onStateChangeCallback(this.state);
    }
  }

  private loadLabel() {
    const x = this.container.width() / 2;
    const y = this.container.height() / 2;
    this.label.setAttrs({
      x,
      y,
      text: this.state.label,
      fill: "black",
      fontFamily: "Geist Mono",
      fontStyle: "bold",
      strokeWidth: 2,
    });

    this.label.moveToTop();

    this.labelContainer.setAttrs({
      x,
      y,
      width: this.label.width() + PADDING * 4,
      height: this.label.height() + PADDING * 4,
      fill: "#FFFFFF",
      cornerRadius: 100,
    });

    this.label.offset({
      x: this.label.width() / 2,
      y: this.label.height() / 2,
    });

    this.labelContainer.offset({
      x: this.labelContainer.width() / 2,
      y: this.labelContainer.height() / 2,
    });
  }

  private initElements() {
    this.container = new Konva.Rect({
      height: 100,
      strokeWidth: 2,
    });

    this.label = new Konva.Text();
    this.labelContainer = new Konva.Rect();

    this.tableGroup.add(this.container);
    this.tableGroup.add(this.label);
    this.tableGroup.add(this.labelContainer);
  }

  private destroyChildren() {
    this.tableGroup.destroyChildren();

    while (this.seats.length) {
      this.seats.pop();
    }
  }

  private getNextSeatNumber() {
    const n = this.nextSeatNumber.toString();
    this.nextSeatNumber++;
    return n;
  }
}
